package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.MouseEvent

/**
 * The one question standing between a translator and losing unsaved work.
 *
 * A function rather than the top-level `const val` it was: a `const` is initialised with the class,
 * before any catalogue exists, so it could not hold a lookup and the extractor listed it under
 * "Unreached" — the browser's own confirm asked in English in every locale. Read inside the
 * listener, where the catalogue has been loaded for as long as the editor has been on screen.
 */
private fun leaveEditorWarning(): String = tr(
    "editor.editor_draft_protection.leave_the_translation_editor_unsaved_work",
    "Leave the translation editor? Unsaved work is still waiting to be saved.",
)

@Composable
internal fun EditorDraftProtection(hasUnsavedDrafts: Boolean) {
    DisposableEffect(hasUnsavedDrafts) {
        if (!hasUnsavedDrafts) return@DisposableEffect onDispose {}

        val beforeUnload = EventListener { event ->
            event.preventDefault()
            event.asDynamic().returnValue = ""
        }
        val linkGuard = EventListener { rawEvent ->
            val event = rawEvent as? MouseEvent ?: return@EventListener
            if (event.button.toInt() != 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return@EventListener
            }
            val anchor = event.target.nearestAnchor() ?: return@EventListener
            if (anchor.hasAttribute("download") || anchor.target == "_blank") return@EventListener
            val destination = anchor.href.takeIf(String::isNotBlank) ?: return@EventListener
            if (destination == window.location.href || destination.startsWith("javascript:")) return@EventListener
            if (!window.confirm(leaveEditorWarning())) {
                event.preventDefault()
                event.stopImmediatePropagation()
            }
        }

        window.addEventListener("beforeunload", beforeUnload)
        document.addEventListener("click", linkGuard, true)
        onDispose {
            window.removeEventListener("beforeunload", beforeUnload)
            document.removeEventListener("click", linkGuard, true)
        }
    }
}

private fun Any?.nearestAnchor(): HTMLAnchorElement? {
    var element = this as? Element
    while (element != null) {
        if (element is HTMLAnchorElement) return element
        element = element.parentElement
    }
    return null
}
